import { valueof } from '@flipster/utils/typescript';

import { Timestamp } from '../../types';

export type CompetitionType =
  | 'GENERAL'
  | 'SYMBOL_CHAMPIONS'
  | 'WEEKLY_PNL'
  | 'MONTHLY_PNL'
  | 'NEW_LISTING'
  | 'HOSTED_BY_INFLUENCER'
  | 'IMPLICIT_COMPETITION'
  | 'ALLOWLISTED';

export const CompetitionStatusEnum = {
  Upcoming: 'UPCOMING',
  Ongoing: 'ONGOING',
  Finished: 'FINISHED',
  Canceled: 'CANCELED',
} as const;

export type CompetitionStatus = valueof<typeof CompetitionStatusEnum>;
export type RankBy = 'PNL' | 'VOLUME' | 'ROI';
export type RankByList = RankBy[];

export interface Milestone {
  prize: string;
  value: string;
}

export interface RankPrize {
  prize: string;
  rankFrom: number;
  rankTo: number;
}

export interface TradingCompetition {
  backgroundColorBanner: string;
  backgroundColorLeft: string;
  backgroundColorRight: string;
  bannerUrls: {
    desktop: string;
    mobile: string;
  };
  competitionEndTime: Timestamp;
  competitionStartTime: Timestamp;
  competitionStatus: CompetitionStatus;
  competitionType: CompetitionType;
  competitionUrl: string;
  currentMilestoneValue?: string;
  hostName?: string;
  isExclusive: boolean;
  isLeaderboardVisible: boolean;
  meta: {
    description: string;
    title: string;
  };
  needInvitationCode: boolean;
  noticeMarkdown: string;
  participationTime: Timestamp;
  maxPrizePool: {
    pnl: string;
    volume: string;
    roi?: string;
  };
  claimRequiredForReward?: boolean;
  claimedTime?: Timestamp;
  rankBy: RankByList;
  registrationEndTime: Timestamp;
  registrationStartTime: Timestamp;
  registrationStatus: CompetitionStatus;
  title: string;
  tradingSymbolList: string[];
  milestones?: Partial<Record<RankingType, Milestone[]>>;
  maxRankPrizes: {
    pnl: RankPrize[];
    volume: RankPrize[];
    roi?: RankPrize[];
  };
  recurringLastWeekChampion?: {
    nickname: string;
    prize: string;
  };
  previousCompetitionUrl?: string;
}

export interface MyCompetitionRankInfo {
  rank?: number;
  lastRank?: number;
  value: string;
  valueToNextPrizeGroup?: string;
  nextPrizeRank?: number;
}

export interface MyTradingCompetitionInfo {
  lastUpdatedAt?: Timestamp;
  volume: MyCompetitionRankInfo;
  pnl: MyCompetitionRankInfo;
  roi?: MyCompetitionRankInfo;
}

export type RankingType = 'volume' | 'pnl' | 'roi';

export const isRankingType = (value: string | null | undefined): value is RankingType =>
  value === 'volume' || value === 'pnl' || value === 'roi';

export interface TradingCompetitionRankingsParams {
  competitionUrl: string;
  rankingMethod: RankingType;
  offset?: number;
  limit?: number;
  myRanking?: boolean;
}

export interface Ranking {
  accountId: number;
  nickname: string;
  prize?: string;
  value: string;
  rank?: number;
  lastRank?: number;
  userId: number;
  userProfileId?: string;
  profileVisibility?: 'PUBLIC' | 'PRIVATE';
}

export interface TradingCompetitionRankings {
  lastUpdatedTime?: Timestamp;
  pagination: {
    total: number;
    offset: number;
    page: number;
  };
  data: Ranking[];
}
